import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './CoveragePage.css';
import { PageHero } from '../../components/PageHero/PageHero';
import warehousePic from '../../images/hero_warehouse.jpg';

const PRIMARY_STATES = ['FL', 'GA', 'AL', 'MS', 'LA', 'SC', 'NC', 'TN'];
const EXTENDED_STATES = ['TX', 'VA', 'AR', 'KY', 'WV', 'MD', 'DC', 'NY', 'PA', 'OH', 'IN'];

const LANES = [
  { from: 'Tampa, FL', to: 'Atlanta, GA', transit: '24–36h', types: 'FTL & LTL' },
  { from: 'Miami, FL', to: 'Charlotte, NC', transit: '36–48h', types: 'FTL & LTL' },
  { from: 'Tampa, FL', to: 'Houston, TX', transit: '48–72h', types: 'FTL' },
  { from: 'Jacksonville, FL', to: 'Nashville, TN', transit: '24–36h', types: 'LTL' },
  { from: 'Tampa, FL', to: 'New Orleans, LA', transit: '24–48h', types: 'FTL & LTL' },
  { from: 'Tampa, FL', to: 'New York, NY', transit: '72–96h', types: 'FTL' },
];

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

export const CoveragePage = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
      }),
      { threshold: 0.07 }
    );
    sectionRef.current?.querySelectorAll<HTMLElement>('.rn-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Coverage Area — Renuit Logistics</title>
        <meta name="description" content="Renuit covers FL, GA, AL, MS, LA, SC, NC and TN freight lanes. Primary SE US and Gulf Coast corridors — view transit times and full network reach." />
        <link rel="canonical" href="https://www.renuit.pro/coverage" />
        <meta property="og:title" content="Coverage Area — Renuit Logistics" />
        <meta property="og:description" content="Renuit covers FL, GA, AL, MS, LA, SC, NC and TN freight lanes. Primary SE US and Gulf Coast corridors — view transit times and full network reach." />
        <meta property="og:url" content="https://www.renuit.pro/coverage" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content="Coverage Area — Renuit Logistics" />
        <meta name="twitter:description" content="Renuit covers FL, GA, AL, MS, LA, SC, NC and TN freight lanes. Primary SE US and Gulf Coast corridors — view transit times and full network reach." />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
      </Helmet>

      <PageHero
        image={warehousePic}
        tag="Where We Operate"
        heading="Our Coverage Area"
        subheading="Primary focus: Southeast US, Gulf Coast, and East Coast corridors. Extended reach via our vetted national carrier network."
      />

      <section className="rn-coverage" ref={sectionRef}>
        <div className="rn-coverage__inner">

          {/* Map visual */}
          <div className="rn-coverage__map rn-reveal">
            <div className="rn-coverage__map-group">
              <h3 className="rn-coverage__map-label">Primary Coverage</h3>
              <div className="rn-coverage__state-row">
                {PRIMARY_STATES.map(s => (
                  <span key={s} className="rn-coverage__state rn-coverage__state--primary">{s}</span>
                ))}
              </div>
            </div>
            <div className="rn-coverage__map-group">
              <h3 className="rn-coverage__map-label">Extended Network</h3>
              <div className="rn-coverage__state-row">
                {EXTENDED_STATES.map(s => (
                  <span key={s} className="rn-coverage__state rn-coverage__state--extended">{s}</span>
                ))}
              </div>
            </div>
            <p className="rn-coverage__map-note">
              Don't see your state? We cover all 48 contiguous states via our national carrier network —
              request a quote for any lane.
            </p>
          </div>

          {/* Lane cards */}
          <div className="rn-coverage__lanes-header rn-reveal">
            <span className="cs-topper">Primary Lanes</span>
            <h2 className="cs-title">Frequently Served Routes</h2>
          </div>

          <div className="rn-coverage__lanes rn-reveal">
            {LANES.map(({ from, to, transit, types }) => (
              <div key={`${from}-${to}`} className="rn-coverage__lane-card">
                <div className="rn-coverage__lane-route">
                  <span className="rn-coverage__lane-city">{from}</span>
                  <ArrowRight />
                  <span className="rn-coverage__lane-city">{to}</span>
                </div>
                <div className="rn-coverage__lane-meta">
                  <span className="rn-coverage__lane-transit">{transit}</span>
                  <span className="rn-coverage__lane-type">{types}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Warehousing note */}
          <div className="rn-coverage__wh-note rn-reveal">
            <div className="rn-coverage__wh-note-inner">
              <h3 className="rn-coverage__wh-note-title">Warehousing & Last-Mile</h3>
              <p className="rn-coverage__wh-note-body">
                Warehousing and last-mile delivery is currently available in the <strong>Tampa Bay
                metro area</strong>, with planned expansion to Atlanta and Charlotte corridors.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="rn-coverage__cta rn-reveal">
            <p className="rn-coverage__cta-text">Don't see your lane? We can quote any US route.</p>
            <Link to="/quote" className="cs-button-solid">Request a Custom Lane Quote</Link>
          </div>

        </div>
      </section>
    </>
  );
};
